import React, { useState } from 'react';

import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';

import { type KeyPressCallback, useInstantRef, useKeyPress } from '@pkg/hooks';

import { CharacterLimit } from '@app/pages/Prototype/components/PlainTextEditor/components';

import { TextArea, type TextAreaFocusEvent, type TextAreaProps } from '../TextArea';

import './EditableTextArea.scss';

export interface EditableTextAreaProps extends TextAreaProps {
    onCancel?(): void;
    onEnter?(event: KeyboardEvent, value: string): void;
}

export function EditableTextArea({
    id,
    value = '',
    placeholder,
    maxLength,
    disabled = false,
    readOnly = false,
    onChange,
    onEnter,
    onBlur,
    onFocus,
    onCancel,
}: EditableTextAreaProps) {
    const [textAreaElement, textAreaRef] = useInstantRef<HTMLTextAreaElement>();
    const [focused, setFocused] = useState(false);

    function handleBlur(event: TextAreaFocusEvent) {
        setFocused(false);
        onBlur?.(event);
    }

    function handleFocus(event: TextAreaFocusEvent) {
        setFocused(true);
        onFocus?.(event);
    }

    const handleEscape: KeyPressCallback = () => {
        onCancel?.();
    };

    const handleEnter: KeyPressCallback = (event) => {
        setFocused(false);
        onEnter?.(event, value);
        textAreaElement?.blur();
    };

    useKeyPress(['Escape'], handleEscape, {
        target: textAreaElement,
        disabled: disabled || readOnly || !onCancel,
    });

    useKeyPress(['Enter'], handleEnter, {
        target: textAreaElement,
        disabled: disabled || readOnly || !onEnter,
    });

    const canShowCharacterLimit = maxLength && focused && !disabled && !readOnly;

    const characterLimitMarkup = canShowCharacterLimit ? (
        <CharacterLimit limit={maxLength} used={value?.length || 0} contributors={[id]} />
    ) : null;

    const editableTextAreaClassNames = classNames('Yat-EditableTextArea', {
        'Yat-EditableTextArea--hasValue': Boolean(value?.length),
        'Yat-EditableTextArea--disabled': disabled,
        'Yat-EditableTextArea--readOnly': readOnly,
    });

    // Always including a trailing space after `value`.
    // Otherwise, new lines will not compute a new height
    // until a text character is entered.
    const placeholderValue = value ? `${value} ` : placeholder;

    // TODO: Should we wrap with a <label />
    return (
        <div className={editableTextAreaClassNames}>
            <TextArea
                ref={textAreaRef}
                id={id}
                value={value}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
                readOnly={readOnly}
                onChange={onChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
            />

            <span className="Yat-EditableTextArea-Mirror" aria-hidden="true">
                {placeholderValue}
            </span>

            <AnimatePresence>{characterLimitMarkup}</AnimatePresence>
        </div>
    );
}
